import React, { Component } from 'react';
import API from '../../utils/API';
import ViewsContainer from '../../components/views/ViewsContainer';
import LoadingWizard from '../../components/spinner/wizard';
import RFPWizardModal from '../../components/proposal-requests/RFPWizardModal';
import DeleteProposalRequestsModal from './modals/deleteProposalRequestsModal';
import DuplicateProposalRequestModal from './modals/duplicateProposalRequestModal';

let batchCallback;

class ProposalRequests extends Component {
	constructor(props) {
		super(props);

		const fieldColumns = {
			name: 'Request Name',
			eventName: 'Event Name',
			venueCount: 'Venues',
			status: 'Status',
			arrivalDate: 'Arrival Date',
			departureDate: 'Departure Date',
			estimatedAttendees: 'Attendees',
			plannerEmail: 'Planner Email',
			updatedAt: 'Last Updated',
			createdAt: 'Created'
		};

		const fieldCategories = {
			basics: {
				title: 'Basics',
				columns: [
					'name',
					'eventName',
					'venueCount',
					'status',
					'arrivalDate',
					'departureDate',
					'estimatedAttendees',
					'plannerEmail',
					'updatedAt',
					'createdAt'
				]
			}
		};

		const fieldTypes = {
			name: 'string',
			eventName: 'string',
			venueCount: 'number',
			status: 'string',
			arrivalDate: 'date',
			departureDate: 'date',
			estimatedAttendees: 'number',
			plannerEmail: 'string',
			updatedAt: 'timestamp',
			createdAt: 'timestamp'
		};

		this.state = {
			requests: [],
			requestViews: [],
			loading: true,
			createModalOpen: false,
			duplicateModalOpen: false,
			deleteRequestsModalOpen: false,
			checkedRequests: {},
			categoryColumns: fieldCategories,
			columns: fieldColumns,
			columnTypes: fieldTypes
		};
	}

	async componentDidMount() {
		await this.loadRequests();
	}

	loadRequests = async () => {
		this.setState({ loading: true });
		try {
			const requestsRes = await API().get(`Organizations/${this.props.orgId}/proposal-requests`);

			if (requestsRes.data) {
				// Support both array response and { requests, views } object response
				const rawRequests = Array.isArray(requestsRes.data)
					? requestsRes.data
					: (requestsRes.data.requests || []);
				const requestViews = Array.isArray(requestsRes.data)
					? []
					: (requestsRes.data.views || []);

				const mappedRequests = rawRequests.map((request) => {
					// Map data for table display
					request.eventName = request.eventDetails?.eventName || '-';
					request.arrivalDate = request.eventDetails?.arrivalDate || null;
					request.departureDate = request.eventDetails?.departureDate || null;
					request.estimatedAttendees = request.eventDetails?.expectedPeakAttendees || 0;
					request.plannerEmail = request.plannerInfo?.email || '-';
					// venueCount: use proposalVenues array length, fall back to API-provided field
					request.venueCount = Array.isArray(request.proposalVenues)
						? request.proposalVenues.length
						: (request.venueCount ?? 0);
					request.link = `/${this.props.orgId}/proposal-requests/${request._id}`;
					return request;
				});

				// Guard: requestViews must be an array before passing to ViewsContainer
				this.setState({
					requests: mappedRequests,
					requestViews,
					loading: false
				});
			}
		} catch (error) {
			console.error('Error loading proposal requests:', error);
			this.setState({ loading: false });
		}
	};

	toggleCreateModal = () => {
		this.setState({ createModalOpen: !this.state.createModalOpen });
	};

	toggleDuplicateModal = () => {
		this.setState({ duplicateModalOpen: !this.state.duplicateModalOpen });
	};

	duplicateRequest = async (sourceId, newName) => {
		try {
			await API().post(`Organizations/${this.props.orgId}/proposal-requests/${sourceId}/duplicateRequest`, {
				proposalRequestId: sourceId,
				name: newName
			});
			this.setState({ duplicateModalOpen: false });
			await this.loadRequests();
		} catch (error) {
			console.error('Error duplicating proposal request:', error);
			throw error;
		}
	};

	handleCreateRequest = async (requestData) => {
		try {
			await API().post(`Organizations/${this.props.orgId}/proposal-requests`, requestData);
			this.toggleCreateModal();
			await this.loadRequests();
		} catch (error) {
			console.error('Error creating request:', error);
			throw error;
		}
	};

	deleteRequests = async () => {
		const requestIds = Object.keys(this.state.checkedRequests);
		this.setState({ deletedRequests: 0, deletingRequests: true });
		
		for (let i = 0; i < requestIds.length; i++) {
			try {
				await API().delete(`Organizations/${this.props.orgId}/proposal-requests/${requestIds[i]}`);
				this.setState({ deletedRequests: this.state.deletedRequests + 1 });
			} catch (e) {
				console.error('Error deleting request:', e);
			}
			
			if (i === requestIds.length - 1) {
				const requests = this.state.requests.filter((r) => !requestIds.includes(r._id));
				batchCallback && batchCallback('delete');
				this.setState({ deletingRequests: false, checkedRequests: {}, requests: requests, deleteRequestsModalOpen: false });
			}
		}
	};

	toggleDeleteRequests = (checkedRequests, bcb) => {
		batchCallback = bcb;
		this.setState({
			checkedRequests: checkedRequests || {},
			deleteRequestsModalOpen: !this.state.deleteRequestsModalOpen
		});
	};

	render() {
		const {
			loading,
			requests,
			requestViews,
			categoryColumns,
			columns,
			columnTypes,
			createModalOpen,
			duplicateModalOpen
		} = this.state;

		const mainActions = [
			{ type: 'button', onClick: this.toggleCreateModal, label: 'Request Proposal' }
		];

		if (requests.length > 0) {
			mainActions.push({
				type: 'button',
				onClick: this.toggleDuplicateModal,
				label: (
					<span>
						<i className="las la-copy mr-10" />Duplicate Request
					</span>
				)
			});
		}

		return (
			<div className="pageContainer">
				{!loading && (
					<ViewsContainer
						views={requestViews}
						entries={requests}
						categoryColumns={categoryColumns}
						columns={columns}
						columnTypes={columnTypes}
						title="Sourcing Proposals"
						description="Create proposal requests and send to multiple venues simultaneously. Compare responses and track status."
						subtitle={null}
						readOnly={false}
						mainActions={mainActions}
						batchActions={[
							{
								type: 'button',
								onClick: this.toggleDeleteRequests,
								label: 'Delete',
								iconClass: 'las la-trash-alt',
								class: 'danger'
							}
						]}
						enableDownloadViews={true}
						orgId={this.props.orgId}
						type={'ProposalRequest'}
						referenceCollection={'Organizations'}
						referenceId={this.props.orgId}
						fields={null}
						defaultSort={'createdAt'}
						sortDirection={'desc'}
					/>
				)}
				<LoadingWizard text="Loading Proposal Requests" loading={loading} />

			{createModalOpen && (
				<RFPWizardModal
					isOpen={createModalOpen}
					toggle={this.toggleCreateModal}
					orgId={this.props.orgId}
					onSubmit={this.handleCreateRequest}
				/>
			)}

			{this.state.deleteRequestsModalOpen && (
				<DeleteProposalRequestsModal
					isOpen={this.state.deleteRequestsModalOpen}
					count={Object.keys(this.state.checkedRequests).length}
					toggle={this.toggleDeleteRequests}
					delete={this.deleteRequests}
				/>
			)}

			{duplicateModalOpen && (
				<DuplicateProposalRequestModal
					isOpen={duplicateModalOpen}
					toggle={this.toggleDuplicateModal}
					requests={requests}
					handleSubmit={this.duplicateRequest}
				/>
			)}
		</div>
		);
	}
}

export default ProposalRequests;
